
import * as React from 'react';

import { IControlOverrideOption } from '../../models/controls/ControlFacet';
import { createMemoizer } from '../../utilities/Memoizer';
import { IComponentAs, IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';
import { withDisplayName } from '../../utilities/components/Decorators';

/**
 * Represents props for a component which accepts a default renderer.
 * @public
 * @deprecated Use `Pick<IComponentAsProps<TProps>, 'defaultRender'>` instead.
 */
export type IDefaultRenderProps<TProps> = Pick<IComponentAsProps<TProps>, 'defaultRender'>;

/**
 * @public
 * @deprecated Use `IComponentAsProps<TProps>` instead.
 */
export type IPropsWithDefaultRender<TProps> = IComponentAsProps<TProps>;

/**
 * Represents a component type which is an override for a default
 * version of the component.
 * @public
 * @deprecated Use `IComponentAs<TProps>` instead.
 */
export type IOverrideComponentType<TProps> = IComponentAs<TProps>;

function composable<TProps>(outer: IComponentAs<TProps>): (inner: IComponentAs<TProps>) => IComponentAs<TProps> {
  const Outer = outer;

  const outerMemoizer = createMemoizer((inner: IComponentAs<TProps>) => {
    if (outer === inner) {
      throw new Error('Attempted to compose a component with itself.');
    }

    const Inner = inner;

    const innerMemoizer = createMemoizer((defaultRender: IComponentAs<TProps>) => {
      return withDisplayName('Inner')((innerProps: IComponentAsProps<TProps>) => {
        return (
          <Inner { ...innerProps } defaultRender={ defaultRender } />
        );
      });
    });

    return withDisplayName('Outer')((outerProps: IComponentAsProps<TProps>) => {
      const {
        defaultRender
      } = outerProps;

      return (
        <Outer
          { ...outerProps }
          defaultRender={
            defaultRender ? innerMemoizer(defaultRender) : Inner
          }
        />
      );
    });
  });

  return outerMemoizer;
}

/**
 * Defines a control option to compose a component type.
 * This simplifies the process of emitting a component type in a control handler callback,
 * ensuring each emitted type gets access to the original type via the defaultRender props.
 * @public
 */
export function defaultRenderComponentOption<TProps>(): IControlOverrideOption<IComponentAs<TProps>> {
  const memoizer = createMemoizer((current: IComponentAs<TProps>) => {
    return composable(current);
  });

  return {
    merge(
      previous: IComponentAs<TProps> | undefined,
      current: IComponentAs<TProps> | undefined
    ): IComponentAs<TProps> | undefined {
      return current && previous && memoizer(current)(previous) || current || previous;
    }
  };
}
